import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { DashboardHeader } from "../../components/super-admin-dashboard";
import AskAiButton from "../../components/super-admin-dashboard/chatbot/AskAiButton";
import ChatbotPanel from "../../components/super-admin-dashboard/chatbot/ChatbotPanel";
import AccountSelector from "../../components/super-admin-dashboard/AccountSelector";
import {ClinicalOverviewTab,FinancialsTab,CareManagerTab,InventoryTab} from "./tabs";
import styles from "./SuperAdminDashboardPage.module.css";
import {streamAIQuery,generateSessionId,getSchemaFromAccount} from "../../services/super-admin-dashboard/aiChatService";

const SuperAdminDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "clinical-overview"
  );

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamAbortRef = useRef(null);

  const [selectedAccount, setSelectedAccount] = useState({
    id: "all",
    name: "All Accounts",
    scope: "all",
  });

  const [chatStateByAccount, setChatStateByAccount] = useState(() => ({
    [selectedAccount.id]: {
      sessionId: generateSessionId(selectedAccount.id),
      messages: [],
    },
  }));

  const abortActiveStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current();
      streamAbortRef.current = null;
    }
  }, []);

  const ensureAccountChatState = useCallback((accountId) => {
    setChatStateByAccount((prev) => {
      if (prev[accountId]) return prev;
      return {
        ...prev,
        [accountId]: {
          sessionId: generateSessionId(accountId),
          messages: [],
        },
      };
    });
  }, []);

  const updateAiMessageForAccount = useCallback((accountId, messageId, textUpdater) => {
    setChatStateByAccount((prev) => {
      const accountState = prev[accountId];
      if (!accountState) return prev;
      return {
        ...prev,
        [accountId]: {
          ...accountState,
          messages: accountState.messages.map((msg) =>
            msg.id === messageId
              ? { ...msg, text: textUpdater(msg.text) }
              : msg
          ),
        },
      };
    });
  }, []);

  useEffect(() => {
    // Ensure we abort any active stream when switching accounts.
    abortActiveStream();
    setIsStreaming(false);

    ensureAccountChatState(selectedAccount.id);
  }, [abortActiveStream, ensureAccountChatState, selectedAccount.id]);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) setActiveTab(tabParam);
  }, [searchParams]);

  const currentChat =
    chatStateByAccount[selectedAccount.id] ||
    { sessionId: generateSessionId(selectedAccount.id), messages: [] };
  const chatMessages = currentChat.messages;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const openChat = () => {
    // abort any existing stream
    abortActiveStream();

    setIsChatOpen(true);
  };

  const handleNewChat = () => {
    abortActiveStream();

    setChatStateByAccount((prev) => ({
      ...prev,
      [selectedAccount.id]: {
        sessionId: generateSessionId(selectedAccount.id),
        messages: [],
      },
    }));

    setIsStreaming(false);
    setIsChatOpen(true);
  };

  const closeChat = () => {
    abortActiveStream();
    setIsChatOpen(false);
  };

  const handleChatSend = (text) => {
    if (!text.trim()) return;

    const accountId = selectedAccount.id;
    const schema = getSchemaFromAccount(selectedAccount);

    const userMessage = {
      id: Date.now(),
      role: "user",
      text,
    };

    const aiMessageId = Date.now() + 1;

    const aiMessage = {
      id: aiMessageId,
      role: "ai",
      text: "",
    };

    const current =
      chatStateByAccount[accountId] ||
      { sessionId: generateSessionId(accountId), messages: [] };

    setChatStateByAccount((prev) => {
      const accountState =
        prev[accountId] ||
        { sessionId: current.sessionId, messages: [] };

      return {
        ...prev,
        [accountId]: {
          ...accountState,
          messages: [...accountState.messages, userMessage, aiMessage],
        },
      };
    });

    setIsChatOpen(true);
    setIsStreaming(true);

    let userId = "default_user";
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = String(user.auth_user_id || "default_user");
      }
    } catch {}

    streamAbortRef.current = streamAIQuery(
      text,
      schema,
      current.sessionId,
      userId,
      {
        onToken: (token) => {
          updateAiMessageForAccount(accountId, aiMessageId, (prevText) => prevText + token);
        },

        onComplete: () => {
          setIsStreaming(false);
          streamAbortRef.current = null;
        },

        onError: (err) => {
          const errorMessage = err?.message || "Something went wrong. Please try again.";
          updateAiMessageForAccount(accountId, aiMessageId, () => errorMessage);
          setIsStreaming(false);
          streamAbortRef.current = null;
        },
      }
    );
  };

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft size={18} />
          </button>
          <DashboardHeader />
        </div>

        <div className={styles.headerRight}>
          <AccountSelector
            value={selectedAccount}
            onChange={setSelectedAccount}
          />
          <AskAiButton
            onClick={() => (isChatOpen ? closeChat() : openChat())}
            isActive={isChatOpen}
          />
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tab} ${
            activeTab === "clinical-overview" ? styles.tabActive : ""
          }`}
          onClick={() => handleTabChange("clinical-overview")}
        >
          Clinical Overview
        </button>

        <button
          className={`${styles.tab} ${
            activeTab === "financials" ? styles.tabActive : ""
          }`}
          onClick={() => handleTabChange("financials")}
        >
          Financials
        </button>

        <button
          className={`${styles.tab} ${
            activeTab === "care-manager" ? styles.tabActive : ""
          }`}
          onClick={() => handleTabChange("care-manager")}
        >
          Care Manager
        </button>
      </div>

      <div className={styles.dashboardContainer}>
        {activeTab === "clinical-overview" && (
          <ClinicalOverviewTab scope={selectedAccount.scope} />
        )}

        {activeTab === "financials" && (
          <FinancialsTab scope={selectedAccount.scope} />
        )}

        {activeTab === "care-manager" && (
          <CareManagerTab scope={selectedAccount.scope} />
        )}

        {activeTab === "inventory" && <InventoryTab />}
      </div>

      {isChatOpen && (
        <ChatbotPanel
          messages={chatMessages}
          onSend={handleChatSend}
          onNewChat={handleNewChat}
          onClose={closeChat}
          isStreaming={isStreaming}
        />
      )}
    </>
  );
};

export default SuperAdminDashboardPage;
